import { getAuthToken, syncApiUrl } from "@cliqsuite/db/powersync";
import { db } from "./db";

/**
 * Offline-write outbox for POS checkout. A sale that couldn't reach sync-api is kept, fully
 * resolved, in the local-only `pending_sales` table (survives reloads and power loss) and
 * replayed once the till is back online. `/sales/orders/commit` is idempotent by order id,
 * so a plain resend is always safe.
 *
 * Deliberately not PowerSync's upload queue: the queue drops a change the server refuses
 * (4xx), and real money must never vanish silently. A refused sale stays here, flagged
 * `needs_attention`, for a manager to resolve.
 */

interface PendingSaleRow {
  id: string;
  request_body: string;
}

/** `orderId` is the primary key, so a resumed attempt after a reload reuses the same order id. */
export async function queueSale(orderId: string, requestBody: Record<string, unknown>): Promise<void> {
  const now = new Date().toISOString();
  // Replace on the same id: settling the same open order again (e.g. a different discount on
  // retry) must replace the earlier attempt. Not an UPSERT: PowerSync tables are SQLite views.
  await db.writeTransaction(async (tx) => {
    const body = JSON.stringify(requestBody);
    // (rowsAffected doesn't count view writes, so check first.)
    if (await tx.getOptional("SELECT 1 FROM pending_sales WHERE id = ?", [orderId])) {
      await tx.execute("UPDATE pending_sales SET request_body = ?, updated_at = ? WHERE id = ?", [body, now, orderId]);
    } else {
      await tx.execute(
        "INSERT INTO pending_sales (id, request_body, queued_at, updated_at, attempts, needs_attention) VALUES (?, ?, ?, ?, 0, 0)",
        [orderId, body, now, now],
      );
    }
  });
}

let flushing = false;

/** Sequential, oldest first. Rows flagged `needs_attention` are skipped: the server already refused them. */
export async function flushOutbox(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const rows = await db.getAll<PendingSaleRow>(
      "SELECT id, request_body FROM pending_sales WHERE needs_attention = 0 ORDER BY queued_at ASC",
    );
    for (const row of rows) await flushOne(row);
  } finally {
    flushing = false;
  }
}

async function flushOne(row: PendingSaleRow): Promise<void> {
  let res: Response;
  try {
    const token = getAuthToken();
    res = await fetch(syncApiUrl("/sales/orders/commit"), {
      method: "POST",
      headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: row.request_body,
      signal: AbortSignal.timeout(30_000),
    });
  } catch {
    await db.execute("UPDATE pending_sales SET attempts = attempts + 1 WHERE id = ?", [row.id]);
    return;
  }
  const now = new Date().toISOString();
  if (res.ok) {
    // The order now exists server-side and syncs down; its web receipt works from here on.
    await db.execute("DELETE FROM pending_sales WHERE id = ?", [row.id]);
    return;
  }
  const detail = `${res.status}: ${await res.text().catch(() => "")}`.slice(0, 500);
  if (res.status >= 400 && res.status < 500) {
    // A real rejection (e.g. a product deleted while offline). Resending can't change it.
    await db.execute("UPDATE pending_sales SET needs_attention = 1, last_error = ?, updated_at = ? WHERE id = ?", [detail, now, row.id]);
    return;
  }
  await db.execute("UPDATE pending_sales SET attempts = attempts + 1, last_error = ?, updated_at = ? WHERE id = ?", [detail, now, row.id]);
}

// ---------------------------------------------------------------------------------------------
// One-time move from the PGlite version. Its IndexedDB may still hold sales queued offline;
// those are real money, so they're copied into pending_sales before the old database is deleted.
// PGlite only loads (lazily) on a till that still has the old database.
const LEGACY_DB = "/pglite/cliqsuite-pos";

let legacyImport: Promise<void> | null = null;
/** Runs once per page load, however many times it's called (StrictMode mounts twice). */
export function importLegacyPendingSales(): Promise<void> {
  legacyImport ??= importLegacy();
  return legacyImport;
}

async function importLegacy(): Promise<void> {
  const existing = await indexedDB.databases?.().catch(() => []);
  if (!existing?.some((d) => d.name === LEGACY_DB)) return;
  const { PGlite } = await import("@electric-sql/pglite");
  const old = await PGlite.create({ dataDir: "idb://cliqsuite-pos" });
  try {
    const hasTable = await old.query<{ n: number }>(
      "SELECT count(*)::int AS n FROM information_schema.tables WHERE table_name = 'pending_sales'",
    );
    if (hasTable.rows[0]?.n) {
      const { rows } = await old.query<{
        id: string;
        request_body: unknown;
        queued_at: string;
        updated_at: string;
        attempts: number;
        last_error: string | null;
        needs_attention: boolean;
      }>("SELECT * FROM pending_sales");
      for (const r of rows) {
        // Check-then-insert (PowerSync tables are views: no UPSERT / OR IGNORE).
        if (await db.getOptional("SELECT 1 FROM pending_sales WHERE id = ?", [r.id])) continue;
        await db.execute(
          `INSERT INTO pending_sales (id, request_body, queued_at, updated_at, attempts, last_error, needs_attention)
           VALUES (?, ?, ?, ?, ?, ?, ?)`,
          [r.id, JSON.stringify(r.request_body), r.queued_at, r.updated_at, r.attempts, r.last_error, r.needs_attention ? 1 : 0],
        );
      }
      if (rows.length) console.info(`Moved ${rows.length} queued offline sale(s) from the old database.`);
    }
  } finally {
    await old.close();
  }
  // Only reached once every row is safely in the new database.
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(LEGACY_DB);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}

